import {
  AxisLabelsFormatterCallbackFunction,
  AxisLabelsFormatterContextObject, Point, Series,
  SeriesSankeyDataLabelsFormatterContextObject,
  TooltipFormatterContextObject,
} from 'highcharts'
import { DateTime } from 'luxon'
import { Ref } from 'vue'

import { DATA_GROUP } from '../../src/constants/common.constant'
import { DATE_FORMAT, DATE_FORMAT_EN } from '../../src/constants/date.constant'
import { ITooltipOptions, IViewPeriodOptions, IXAxisOptions, IYAxisCommonOptions } from '../../src/types/chart-options.type'
import { IPointExtendNode, ISankeyTooltipFormatterObject } from '../../src/types/chart-tooltip.type'
import { IDefaultSeries } from '../../src/types/default-series.type'
import { currencyFormatter } from './currency'

const tooltipHtml = `<div class="xangle-charts-tooltip"
style="padding: 10px; box-sizing: border-box; border: 1px solid rgba(var(--bc-gray), var(--o02)); border-radius: 8px;
background-color: rgba(var(--bc-white), var(--o08)); backdrop-filter: blur(8px); box-shadow: 0 8px 16px 0 rgba(var(--bc-dark), var(--o01));
color:rgb(var(--bc-dark)); display: flex; flex-direction: column; justify-content: flex-start; align-items: flex-start; gap: 10px;">`

export const legendFormatter = (param: Point | Series, showYAxisPos: boolean) => {
  if (showYAxisPos) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return `<span style="text-decoration: none">${param.name}(${param.userOptions.yAxis === 0 ? 'LHS' : 'RHS'})</span>`
  }
  return `<span style="text-decoration: none">${param.name}</span>`
}

export const xAxisFormatter = (
  xAxis: AxisLabelsFormatterContextObject,
  dateFormatLang: 'ko' | 'en',
  intervalTime: Ref<number>,
  isISOTime?: boolean,
) => {
  const max = xAxis.axis.max
  const min = xAxis.axis.min
  if (!max || !min) return ''

  const xAxisMin = DateTime.fromMillis(min)
  const xAxisMax = DateTime.fromMillis(max)
  const period = xAxisMax.diff(xAxisMin, 'days').days
  if (period > 184) {
    intervalTime.value = 24 * 3600 * 1000
    return DateTime.fromMillis(Number(xAxis.value)).toFormat(
      dateFormatLang === 'en' ?
        DATE_FORMAT_EN.YEAR_MONTH_FORMAT :
        DATE_FORMAT.YEAR_MONTH_FORMAT
    )
  } else if (period > 1.25) {
    // ISO 포메팅 사용시 시간 변경
    if (isISOTime) {
      const isoTime = DateTime.fromMillis(Number(xAxis.value))
        .toJSDate()
        .toISOString()
      return DateTime.fromJSDate(
        new Date(isoTime.replaceAll('Z', '')),
      ).toFormat(
        dateFormatLang === 'en' ?
          DATE_FORMAT_EN.TIME_FORMAT :
          DATE_FORMAT.TIME_FORMAT
      )
    }
    // 23.10.31 정책변경 6개월 이하인 경우에는 모두 MM/dd로 표기
    return DateTime.fromMillis(Number(xAxis.value)).toFormat(
      dateFormatLang === 'en' ?
        DATE_FORMAT_EN.MONTH_DATE_FORMAT :
        DATE_FORMAT.MONTH_DATE_FORMAT
    )
  } else {
    intervalTime.value = 3 * 3600 * 1000
    return DateTime.fromMillis(Number(xAxis.value)).toFormat(
      dateFormatLang === 'en' ?
        DATE_FORMAT_EN.TIME_FORMAT :
        DATE_FORMAT.TIME_FORMAT
    )
  }
}

export const yAxisFormatter = (
  param: IYAxisCommonOptions
): AxisLabelsFormatterCallbackFunction => {
  return function (this) {
    if (param.unit === 'percent' || param.unit === '%') {
      return Number(this.value) < -100 ? '-100%' : `${this.value}%`
    }
    // if (Number(this.value) > 0 && Number(this.value) < 1) {
    //   let result = String(this.value)
    //   if (param.unit) {
    //     if (param.unit === 'KRW' || param.unit === '₩') result = '₩' + result
    //     else if (param.unit === 'USD' || param.unit === '$') result = '$' + result
    //     else result += ` ${param.unit}`
    //   }
    //   return result
    // }
    return currencyFormatter({
      number: this.value,
      unit: param.unit ?? '',
      decimalPoint: param.decimalPoint,
      showDecimalZero: false,
      contraction: param.contraction
    })
  }
}

export const defaultTooltipFormatter = (
  chart: TooltipFormatterContextObject,
  xAxisOption: IXAxisOptions,
  periodOption: IViewPeriodOptions,
  tooltipOption: ITooltipOptions,
  dateFormatLang: 'ko' | 'en'
) => {
  // if (!chart.points) return ''
  let tooltipHeader = ''
  let tooltipDate = '<p><b>'
  let groupPeriod = '</b></p>'
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (!isNaN(Number(chart.x)) && chart.x >= 1230735600000) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
    tooltipDate += `${DateTime.fromMillis(chart.x).toFormat(
      dateFormatLang === 'en' ?
        DATE_FORMAT_EN[tooltipOption.dateFormat] :
        DATE_FORMAT[tooltipOption.dateFormat]
    )}`
    // 2023.03.22 툴팁 기간표기 정책 추가
    let targetTimestamp = 0
    switch (tooltipOption.dataGroup) {
      case DATA_GROUP.WEEK: {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
        targetTimestamp = DateTime.fromMillis(chart.x)
          .plus({ week: 1 })
          .minus({ day: 1 })
          .toMillis()
        break
      }
      case DATA_GROUP.MONTH: {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
        targetTimestamp = DateTime.fromMillis(chart.x)
          .plus({ month: 1 })
          .minus({ day: 1 })
          .toMillis()
        break
      }
      case DATA_GROUP.QUARTER: {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
        targetTimestamp = DateTime.fromMillis(chart.x)
          .plus({ quarter: 1 })
          .minus({ day: 1 })
          .toMillis()
        break
      }
    }
    if (targetTimestamp !== 0) {
      if (DateTime.now().toMillis() - targetTimestamp < 0) {
        groupPeriod = ` ~ ${DateTime.now()
          .minus({ day: 1 })
          .toFormat(
            dateFormatLang === 'en' ?
              DATE_FORMAT_EN[tooltipOption.dateFormat] :
              DATE_FORMAT[tooltipOption.dateFormat]
          )}</b></p>`
      } else {
        groupPeriod = ` ~ ${DateTime.fromMillis(targetTimestamp).toFormat(
          dateFormatLang === 'en' ?
            DATE_FORMAT_EN[tooltipOption.dateFormat] :
            DATE_FORMAT[tooltipOption.dateFormat]
        )}</b></p>`
      }
    }
    tooltipDate += groupPeriod

    if (tooltipOption.dataGroup === DATA_GROUP.AGGREGATION && xAxisOption.standard === 'day' && periodOption.stDate && periodOption.endDate) {
      tooltipDate = `<p><b>${periodOption.stDate} ~ ${periodOption.endDate}</b></p>`
    }
  } else if (typeof chart.x === 'string') {
    if (xAxisOption.title) {
      tooltipDate += `${xAxisOption.title}: ${chart.x}</b></p>`
    } else {
      tooltipDate += `${chart.x}</b></p>`
    }
  }
  // ISO 포메팅 사용시 시간 변경
  // if (tooltipOption.useISOTime) {
  //   const isoTime = DateTime.fromMillis(chart.x).toJSDate().toISOString()
  //   tooltipDate = `<p><b>${DateTime.fromJSDate(
  //     new Date(isoTime.replaceAll('Z', '')),
  //   ).toFormat(DATE_FORMAT.TIME_FORMAT)}</b></p>`
  // }

  let tooltipContent = ''
  if (chart.points) {
    // 불필요 정책 임시 주석처리
    // // Line, Area, Stacked // Line, Area, Stacked Bar 차트 경우에는 툴팁 오름차순
    //     // if (
    //     //   !chart.points.find(ele => ele.point.series.userOptions.type !== CHART_TYPE.LINE)
    //     // ) {
    //     //   if (
    //     //     !chart.points.find(
    //     //       ele =>
    //     //         ele.series.name !== 'movingAvg',
    //     //     )
    //     //   ) {
    //     //     // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //     //     // @ts-ignore
    //     //     chart.points.sort((a, b) => b.y - a.y)
    //     //   }
    //     // }
    //     // if (
    //     //   !chart.points.find(ele => ele.point.series.userOptions.type !== CHART_TYPE.AREA)
    //     // ) {
    //     //   // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //     //   // @ts-ignore
    //     //   chart.points.sort((a, b) => b.y - a.y)
    //     // }Bar 차트 경우에는 툴팁 오름차순
    // if (
    //   !chart.points.find(ele => ele.point.series.userOptions.type !== CHART_TYPE.LINE)
    // ) {
    //   if (
    //     !chart.points.find(
    //       ele =>
    //         ele.series.name !== 'movingAvg',
    //     )
    //   ) {
    //     // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //     // @ts-ignore
    //     chart.points.sort((a, b) => b.y - a.y)
    //   }
    // }
    // if (
    //   !chart.points.find(ele => ele.point.series.userOptions.type !== CHART_TYPE.AREA)
    // ) {
    //   // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //   // @ts-ignore
    //   chart.points.sort((a, b) => b.y - a.y)
    // }
    // todo: 확인필요
    // if (
    //   !chart.points.find(
    //     ele => ele.point.series.userOptions.type !== 'column',
    //   ) &&
    //   defaultOption.value?.plotOptions?.column?.stacking
    // ) {
    //   chart.points.sort((a, b) => b.y - a.y)
    // }

    chart.points.forEach((point, index) => {
      if (index > 15) return

      if (tooltipOption.title) {
        tooltipHeader = `<p><b>${tooltipOption.title}</b></p>`
      }

      const chartColor = point.color
      const dataName = point.series.name

      // arearange 차트의 경우
      if ('low' in point.point && 'high' in point.point) {
        tooltipContent += `<p>&nbsp;<span style="color:${chartColor};">● </span>${dataName}: ${currencyFormatter(
          {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
            number: point.point.high - point.point.low,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            unit: point.series.userOptions.stacking === 'percent' ? '%' : point.series.userOptions.unit,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            decimalPoint: point.series.userOptions.decimalPoint,
            showDecimalZero: true,
            contraction: tooltipOption.contraction,
          },
        )} 
</p>`
        // ~ ${currencyFormatter(
        //           {
        //           // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //           // @ts-ignore
        //             number: point.point.high,
        //             // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //             // @ts-ignore
        //             unit: point.series.userOptions.stacking === 'percent' ? '%' : point.series.userOptions.unit,
        //             // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //             // @ts-ignore
        //             decimalPoint: point.series.userOptions.decimalPoint,
        //             showDecimalZero: true,
        //             contraction: tooltipOption.contraction,
        //           },
        //         )}
      } else {
        tooltipContent += `<p>&nbsp;<span style="color:${chartColor};">● </span>${dataName}: ${currencyFormatter(
          {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
            number: Number(point.series.userOptions.stacking === 'percent' ? point.percentage : point.y),
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            unit: point.series.userOptions.stacking === 'percent' ? '%' : point.series.userOptions.unit,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            decimalPoint: point.series.userOptions.decimalPoint,
            showDecimalZero: tooltipOption.showDecimalZero,
            contraction: tooltipOption.contraction,
          },
        )}</p>`
      }
    })
  } else {
    // scatter 차트
    const chartColor = chart.series.color
    const dataName = chart.series.name

    tooltipContent += `<p>&nbsp;<span style="color:${chartColor};">● </span>${dataName}: ${currencyFormatter(
      {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        number: Number(chart.series.userOptions.stacking === 'percent' ? chart.percentage : chart.y),
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        unit: chart.series.userOptions.stacking === 'percent' ? '%' : chart.series.userOptions.unit,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        decimalPoint: chart.series.userOptions.decimalPoint,
        showDecimalZero: true,
        contraction: tooltipOption.contraction,
      },
    )}</p>`
  }
  return `${tooltipHtml}<div>${tooltipHeader}${tooltipDate}</div><div>${tooltipContent}</div></div>`
}

export const pieChartTooltipFormatter = (
  chart: TooltipFormatterContextObject,
  tooltipOption: ITooltipOptions,
) => {
  if (!chart || !chart.point) return ''
  const tooltipHeader = `<p><span style="color:${
    chart.point.color
  };">●&nbsp;&nbsp;</span>${chart.point.name}</p>`
  const tooltipContents = `<div style="width: 100%; display: flex; justify-content: space-between; align-items: center; gap: 40px;">
    <p>${tooltipOption.commonContents}</p>
    <p><b>${currencyFormatter({
    number: chart.point.y ?? 0,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    unit: chart.point.options.unit,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    decimalPoint: chart.point.options.decimalPoint,
    showDecimalZero: true,
  })}</b></p>
</div>`
  const tooltipRatio = `<div style="width: 100%; display: flex; justify-content: space-between; align-items: center; gap: 40px;">
    <p>Ratio</p>
    <p><b>${chart.point.percentage?.toFixed(2)}%</b></p>
</div>`
  return tooltipHtml + tooltipHeader + tooltipContents + tooltipRatio + '</div>'
}

export const sankeyNodeFormatter = (node: SeriesSankeyDataLabelsFormatterContextObject & IPointExtendNode) => {
  const name = node.key
  let percent = ''
  let decimalPoint = 2
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const dataList = node.point.series.userOptions.data.filter(ele => ele.name === node.key)
  dataList.forEach((ele: IDefaultSeries) => {
    if (decimalPoint < ele.decimalPoint) {
      decimalPoint = ele.decimalPoint
    }
  })

  if (node.point.linksTo && node.point.linksTo.length === 1) {
    if (node.point.linksTo[0].fromNode && node.point.linksTo[0].fromNode.sum) {
      percent = currencyFormatter({
        number: node.point.sum / node.point.linksTo[0].fromNode.sum * 100,
        unit: '%',
        decimalPoint,
      })
    }
  } else if (node.point.linksTo && node.point.linksTo.length === 0) {
    percent = '100%'
  }
  return `${name}<br>${percent}`
}

export const sankeyChartTooltipFormatter = (
  chart: ISankeyTooltipFormatterObject,
  tooltipOption: ITooltipOptions,
) => {
  let tooltipBody = ''
  let fromName = ''
  let fromValue = 0
  let toName = ''
  let toValue = 0
  // 라인에 마우스 올렸을 때
  if (chart.point.fromNode) {
    fromName = chart.point.from
    fromValue = chart.point.fromNode.sum
    toName = chart.point.to
    toValue = chart.point.weight

    tooltipBody += `
      <div style="width: 100%; display: flex; justify-content: space-between; align-items: center; gap: 40px;">
        <p>${fromName}</p>
        <p>${fromValue}</p>
      </div>`
  }
  // 노드에 마우스 올렸을 때
  else if (chart.point.linksTo && chart.point.linksTo.length === 1) {
    fromName = chart.point.linksTo[0].fromNode?.name ?? ''
    fromValue = chart.point.linksTo[0].fromNode?.sum ?? 0
    toName = chart.point.name
    toValue = chart.point.sum
  }

  tooltipBody += `
      <div style="width: 100%; display: flex; justify-content: space-between; align-items: center; gap: 40px;">
        <p>${toName}</p>
        <p>${toValue}</p>
      </div>
      <div style="width: 100%; display: flex; justify-content: space-between; align-items: center; gap: 32px;">
        <p>Circulating ${toName} to ${fromName}</p>
        <p>${currencyFormatter({ number: toValue / fromValue * 100, unit: '%', decimalPoint: 2 })}</p>
      </div>`

  return tooltipHtml + tooltipBody + '</div>'
}